import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import { createCaseAccessToken } from './caseAccess';
import {
  syncAdvocateClientAccess,
  syncCaseAccessComment,
  syncCaseAccessDocument,
  syncCaseAccessPayment,
  syncCaseAccessRecord,
} from './clientAccessRecords';
import { createLifecycleStep } from './lifecycle';

const formatDate = (date) => date.toISOString().split('T')[0];

const dateAfter = (days) => {
  const nextDate = new Date();
  nextDate.setDate(nextDate.getDate() + days);
  return formatDate(nextDate);
};

const buildLifecycle = (overrides = {}) => [
  createLifecycleStep({
    id: 'consultation',
    title: 'Initial consultation',
    eta: overrides.consultationEta || '2026-04',
    status: overrides.consultation || 'done',
  }),
  createLifecycleStep({
    id: 'drafting',
    title: 'Draft petition and evidence set',
    eta: overrides.draftingEta || '2026-05',
    status: overrides.drafting || 'in_progress',
  }),
  createLifecycleStep({
    id: 'filing',
    title: 'File before court',
    eta: overrides.filingEta || '2026-06',
    status: overrides.filing || 'pending',
  }),
  createLifecycleStep({
    id: 'hearing',
    title: overrides.hearingTitle || 'Interim relief hearing',
    eta: overrides.hearingEta || '2026-07',
    status: overrides.hearing || 'pending',
    stageType: 'hearing',
    scheduledDate: overrides.hearingDate || '',
    notes: overrides.hearingNotes || '',
  }),
  createLifecycleStep({
    id: 'closure',
    title: 'Order follow-up and closure',
    eta: overrides.closureEta || '2026-08',
    status: overrides.closure || 'pending',
  }),
];

const advocateClients = (advocateId) => [
  {
    advocate_id: advocateId,
    name: 'Demo Client Asha',
    phone: '9000000001',
    email: 'demo.client.asha@example.com',
    preferredLanguage: 'en',
    relationLabel: 'S/o',
    relationName: 'Demo Parent One',
    age: '34',
    dateOfBirth: '1992-08-11',
    gender: 'Male',
    address: 'Demo Address 101, Sample Nagar, New Delhi',
    aadhaarName: 'Demo Client Asha',
    aadhaarNumber: '0000 0000 0001',
  },
  {
    advocate_id: advocateId,
    name: 'Demo Client Bhavna',
    phone: '9000000002',
    email: 'demo.client.bhavna@example.com',
    preferredLanguage: 'hi',
    relationLabel: 'D/o',
    relationName: 'Demo Parent Two',
    age: '31',
    dateOfBirth: '1995-01-24',
    gender: 'Female',
    address: 'Demo Address 202, Sample Layout, Bengaluru',
    aadhaarName: 'Demo Client Bhavna',
    aadhaarNumber: '0000 0000 0002',
  },
  {
    advocate_id: advocateId,
    name: 'Demo Client Charan',
    phone: '9000000003',
    email: 'demo.client.charan@example.com',
    preferredLanguage: 'en',
    relationLabel: 'S/o',
    relationName: 'Demo Parent Three',
    age: '39',
    dateOfBirth: '1987-04-03',
    gender: 'Male',
    address: 'Demo Address 303, Sample Road, Mumbai',
    aadhaarName: 'Demo Client Charan',
    aadhaarNumber: '0000 0000 0003',
  },
];

const advocateCases = (advocateId) => [
  {
    advocate_id: advocateId,
    case_number: 'DEMO-CIV-001/2026',
    client_name: 'Demo Client Asha',
    client_email: 'demo.client.asha@example.com',
    client_phone: '9000000001',
    advocate_language: 'en',
    client_language: 'en',
    status: 'Open',
    court: 'Demo District Court',
    place: 'Demo City',
    police_station: 'Demo Police Station One',
    summary: 'Demo civil recovery matter involving sample commercial dues and interim relief.',
    next_step: 'Prepare sample reply bundle and supporting invoice set.',
    lifecycle: buildLifecycle({
      consultation: 'done',
      drafting: 'done',
      filing: 'in_progress',
      hearingDate: dateAfter(1),
      hearingNotes: 'Reply filing and interim relief hearing',
    }),
    client_access_token: createCaseAccessToken('DEMO-CIV-001/2026'),
    client_access_enabled: true,
  },
  {
    advocate_id: advocateId,
    case_number: 'DEMO-FAM-002/2026',
    client_name: 'Demo Client Bhavna',
    client_email: 'demo.client.bhavna@example.com',
    client_phone: '9000000002',
    advocate_language: 'en',
    client_language: 'hi',
    status: 'Pending',
    court: 'Demo Family Court',
    place: 'Demo City',
    police_station: 'Demo Police Station Two',
    summary: 'Demo family matter currently at counselling and settlement review stage.',
    next_step: 'Collect sample counselling note and update filing pack.',
    lifecycle: buildLifecycle({
      consultation: 'done',
      drafting: 'in_progress',
      hearingTitle: 'Counselling progress hearing',
      hearingDate: dateAfter(3),
      hearingNotes: 'Counselling progress review',
    }),
    client_access_token: createCaseAccessToken('DEMO-FAM-002/2026'),
    client_access_enabled: true,
  },
  {
    advocate_id: advocateId,
    case_number: 'DEMO-COM-003/2026',
    client_name: 'Demo Client Charan',
    client_email: 'demo.client.charan@example.com',
    client_phone: '9000000003',
    advocate_language: 'en',
    client_language: 'en',
    status: 'Open',
    court: 'Demo Commercial Court',
    place: 'Demo City',
    police_station: 'Demo Police Station Three',
    summary: 'Demo commercial dispute with current hearing preparation underway.',
    next_step: 'Prepare sample final argument note and chronology.',
    lifecycle: buildLifecycle({
      consultation: 'done',
      drafting: 'done',
      filing: 'done',
      hearing: 'in_progress',
      hearingTitle: 'Arguments hearing',
      hearingDate: dateAfter(5),
      hearingNotes: 'Arguments on maintainability',
    }),
    client_access_token: createCaseAccessToken('DEMO-COM-003/2026'),
    client_access_enabled: true,
  },
];

const advocatePayments = (advocateId, cases) => [
  {
    advocate_id: advocateId,
    case_id: 'DEMO-CIV-001/2026',
    amount: 25000,
    date: dateAfter(-5),
    description: 'Drafting and filing fee',
    stage: 'Filing',
    status: 'Paid',
    requested_from_client: true,
    client_access_token: cases[0].client_access_token,
  },
  {
    advocate_id: advocateId,
    case_id: 'DEMO-FAM-002/2026',
    amount: 12000,
    date: dateAfter(-2),
    description: 'Consultation and appearance',
    stage: 'Consultation',
    status: 'Paid',
    requested_from_client: true,
    client_access_token: cases[1].client_access_token,
  },
  {
    advocate_id: advocateId,
    case_id: 'DEMO-COM-003/2026',
    amount: 18000,
    date: dateAfter(2),
    description: 'Arguments preparation milestone',
    stage: 'Arguments',
    status: 'Requested',
    requested_from_client: true,
    client_access_token: cases[2].client_access_token,
  },
];

const advocateDocuments = (advocateId, cases) => [
  {
    advocate_id: advocateId,
    case_id: 'DEMO-CIV-001/2026',
    type: 'Affidavit',
    url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
    name: 'draft-affidavit.pdf',
    uploaded_by_role: 'advocate',
    client_access_token: cases[0].client_access_token,
  },
  {
    advocate_id: advocateId,
    case_id: 'DEMO-FAM-002/2026',
    type: 'Client ID Proof',
    url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf',
    name: 'id-proof.pdf',
    uploaded_by_role: 'advocate',
    client_access_token: cases[1].client_access_token,
  },
];

const advocateComments = (advocateId, cases) => [
  {
    advocate_id: advocateId,
    case_id: 'DEMO-CIV-001/2026',
    author_role: 'advocate',
    author_name: 'Advocate',
    message: 'Please review the draft affidavit shared in the documents section.',
    created_at: `${dateAfter(-1)}T10:00:00.000Z`,
    client_access_token: cases[0].client_access_token,
  },
  {
    advocate_id: advocateId,
    case_id: 'DEMO-COM-003/2026',
    author_role: 'advocate',
    author_name: 'Advocate',
    message: 'Filing is complete. We are preparing the next hearing brief.',
    created_at: `${dateAfter(0)}T11:00:00.000Z`,
    client_access_token: cases[2].client_access_token,
  },
];

const adminAlerts = (adminId) => [
  {
    admin_id: adminId,
    title: 'Storage review due',
    detail: 'Document storage crossed 68 percent of the current quota.',
    severity: 'Medium',
  },
  {
    admin_id: adminId,
    title: 'Auth provider check',
    detail: 'Google sign-in enabled for testing accounts.',
    severity: 'Info',
  },
];

const legacyDemoClientNames = [
  'Aarav Mehta',
  'Neha Sharma',
  'Rohan Iyer',
  'R.K. Chaturvedi',
  'Sunita Rao',
  'Ankita Deshmukh',
  'Prakash Nair',
];
const legacyDemoCaseNumbers = [
  'DEL-CIV-204/2026',
  'BLR-FAM-118/2026',
  'MUM-COM-077/2026',
  'DL-BAIL-101/2026',
  'DL-NI138-233/2026',
  'PN-FAM-044/2026',
  'PN-COM-188/2026',
];

const deleteDocs = async (docs = []) => {
  await Promise.all(docs.map((item) => deleteDoc(item.ref)));
};

const clearClientAccessToken = async (token) => {
  if (!token) return;

  const paymentDocs = await getDocs(collection(db, 'client_access', token, 'payments'));
  const documentDocs = await getDocs(collection(db, 'client_access', token, 'documents'));
  const commentDocs = await getDocs(collection(db, 'client_access', token, 'comments'));

  await Promise.all([
    deleteDocs(paymentDocs.docs),
    deleteDocs(documentDocs.docs),
    deleteDocs(commentDocs.docs),
  ]);

  await deleteDoc(doc(db, 'client_access', token));
};

const seedCollectionIfEmpty = async (collectionName, fieldName, fieldValue, records) => {
  const collectionRef = collection(db, collectionName);
  const snapshot = await getDocs(query(collectionRef, where(fieldName, '==', fieldValue)));

  if (snapshot.empty) {
    const docs = [];
    for (const record of records) {
      const docRef = await addDoc(collectionRef, record);
      docs.push({ id: docRef.id, ...record });
    }
    return docs;
  }

  return snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }));
};

export const seedAdvocateData = async (advocateId) => {
  if (!advocateId) return;

  const seedKey = `seeded_${advocateId}_advocate_v9`;
  if (localStorage.getItem(seedKey) === 'true') return;

  const cases = advocateCases(advocateId);
  await seedCollectionIfEmpty('clients', 'advocate_id', advocateId, advocateClients(advocateId));
  const seededCases = await seedCollectionIfEmpty('cases', 'advocate_id', advocateId, cases);
  const seededPayments = await seedCollectionIfEmpty('payments', 'advocate_id', advocateId, advocatePayments(advocateId, cases));
  const seededDocuments = await seedCollectionIfEmpty('documents', 'advocate_id', advocateId, advocateDocuments(advocateId, cases));
  const seededComments = await seedCollectionIfEmpty('comments', 'advocate_id', advocateId, advocateComments(advocateId, cases));

  for (const caseRecord of seededCases) {
    await syncCaseAccessRecord(caseRecord);
  }

  for (const payment of seededPayments) {
    if (payment.client_access_token) {
      await syncCaseAccessPayment(payment.client_access_token, payment, payment.id);
    }
  }

  for (const documentRecord of seededDocuments) {
    if (documentRecord.client_access_token) {
      await syncCaseAccessDocument(documentRecord.client_access_token, documentRecord, documentRecord.id);
    }
  }

  for (const comment of seededComments) {
    if (comment.client_access_token) {
      await syncCaseAccessComment(comment.client_access_token, comment, comment.id);
    }
  }

  await syncAdvocateClientAccess(advocateId);

  localStorage.setItem(seedKey, 'true');
};

export const cleanupLegacyAdvocateDemoData = async (advocateId) => {
  if (!advocateId) return false;

  const cleanupKey = `cleanup_${advocateId}_legacy_demo_v2`;
  if (localStorage.getItem(cleanupKey) === 'true') return false;

  const [clientsSnapshot, casesSnapshot, paymentsSnapshot, documentsSnapshot, commentsSnapshot] = await Promise.all([
    getDocs(query(collection(db, 'clients'), where('advocate_id', '==', advocateId))),
    getDocs(query(collection(db, 'cases'), where('advocate_id', '==', advocateId))),
    getDocs(query(collection(db, 'payments'), where('advocate_id', '==', advocateId))),
    getDocs(query(collection(db, 'documents'), where('advocate_id', '==', advocateId))),
    getDocs(query(collection(db, 'comments'), where('advocate_id', '==', advocateId))),
  ]);

  const clientDocs = clientsSnapshot.docs.filter((item) => legacyDemoClientNames.includes(item.data().name));
  const caseDocs = casesSnapshot.docs.filter((item) => legacyDemoCaseNumbers.includes(item.data().case_number));
  const demoCaseNumbers = new Set(caseDocs.map((item) => item.data().case_number));
  const demoTokens = [...new Set(caseDocs.map((item) => item.data().client_access_token).filter(Boolean))];

  const paymentDocs = paymentsSnapshot.docs.filter((item) => demoCaseNumbers.has(item.data().case_id));
  const documentDocs = documentsSnapshot.docs.filter((item) => demoCaseNumbers.has(item.data().case_id));
  const commentDocs = commentsSnapshot.docs.filter((item) => demoCaseNumbers.has(item.data().case_id));

  const hasDemoData = clientDocs.length || caseDocs.length || paymentDocs.length || documentDocs.length || commentDocs.length;

  if (!hasDemoData) {
    localStorage.setItem(cleanupKey, 'true');
    return false;
  }

  await Promise.all([
    deleteDocs(paymentDocs),
    deleteDocs(documentDocs),
    deleteDocs(commentDocs),
    deleteDocs(caseDocs),
    deleteDocs(clientDocs),
    Promise.all(demoTokens.map((token) => clearClientAccessToken(token))),
  ]);

  localStorage.setItem(cleanupKey, 'true');
  return true;
};

export const seedAdminData = async (adminId) => {
  if (!adminId) return;

  const seedKey = `seeded_${adminId}_admin_v1`;
  if (localStorage.getItem(seedKey) === 'true') return;

  await seedCollectionIfEmpty('system_alerts', 'admin_id', adminId, adminAlerts(adminId));
  localStorage.setItem(seedKey, 'true');
};

export const ensureAdminUserDoc = async (adminUser) => {
  if (!adminUser?.uid) return;

  const userRef = doc(db, 'users', adminUser.uid);
  const snapshot = await getDoc(userRef);

  if (!snapshot.exists()) {
    await setDoc(userRef, {
      uid: adminUser.uid,
      email: adminUser.email || '',
      role: 'admin',
      name: adminUser.displayName || 'System Admin',
      createdAt: new Date().toISOString(),
      profileComplete: true,
    });
  }
};
